import { useMemo } from 'react'

/**
 * FallingPetals — animated falling petal overlay.
 * Place as first child of a `position:relative` container.
 * pointer-events:none so it never blocks clicks.
 */
export default function FallingPetals({ density = 18 }) {
  // Generate stable petal data once per mount using a seeded approach
  const petals = useMemo(() => {
    // Simple deterministic noise so petals are stable across re-renders
    function noise(i, salt) {
      const x = Math.sin(i * 9301 + salt * 49297 + 233) * 43758.5453
      return x - Math.floor(x) // 0..1
    }
    return Array.from({ length: density }, (_, i) => {
      const isIvory   = i % 2 === 0
      const w         = 8  + noise(i, 1) * 8          // 8–16 px wide
      const h         = w  * (1.5 + noise(i, 2) * 0.4) // 1.5–1.9× tall
      const duration  = 18 + noise(i, 3) * 6           // 18–24 s
      const delay     = -(noise(i, 4) * 10)            // −0..−10 s (pre-offset)
      const left      = noise(i, 5) * 100              // 0–100 %
      const swayDir   = noise(i, 6) > 0.5 ? 'A' : 'B' // which sway pattern

      return { i, isIvory, w, h, duration, delay, left, swayDir,
        color:   isIvory ? '#F5EFE6' : '#C4826A',
        opacity: isIvory ? 0.52      : 0.40,
      }
    })
  }, [density])

  return (
    <div aria-hidden="true" style={{
      position:'absolute', inset:0, overflow:'hidden',
      pointerEvents:'none', zIndex:0,
    }}>
      <style>{`
        /* Vertical fall — top offscreen → below container */
        @keyframes fp-fall {
          0%   { transform: translateY(-64px); opacity: 0; }
          7%   { opacity: 1; }
          88%  { opacity: 0.75; }
          100% { transform: translateY(110vh);  opacity: 0; }
        }

        /* Sway right-first + clockwise rotation */
        @keyframes fp-swayA {
          0%   { transform: translateX(0px)   rotate(0deg);   }
          20%  { transform: translateX(38px)  rotate(72deg);  }
          40%  { transform: translateX(-28px) rotate(144deg); }
          60%  { transform: translateX(40px)  rotate(216deg); }
          80%  { transform: translateX(-22px) rotate(288deg); }
          100% { transform: translateX(0px)   rotate(360deg); }
        }

        /* Sway left-first + counter-clockwise rotation */
        @keyframes fp-swayB {
          0%   { transform: translateX(0px)   rotate(0deg);    }
          20%  { transform: translateX(-38px) rotate(-72deg);  }
          40%  { transform: translateX(30px)  rotate(-144deg); }
          60%  { transform: translateX(-40px) rotate(-216deg); }
          80%  { transform: translateX(24px)  rotate(-288deg); }
          100% { transform: translateX(0px)   rotate(-360deg); }
        }
      `}</style>

      {petals.map(p => (
        /* Outer div: vertical fall */
        <div key={p.i} style={{
          position:  'absolute',
          left:      `${p.left}%`,
          top:       0,
          animation: `fp-fall ${p.duration}s ${p.delay}s linear infinite`,
          willChange:'transform, opacity',
        }}>
          {/* Inner div: horizontal sway + rotation */}
          <div style={{
            animation: `fp-sway${p.swayDir} ${p.duration}s ${p.delay}s ease-in-out infinite`,
            willChange:'transform',
          }}>
            {/* Petal SVG — teardrop/leaf shape */}
            <svg
              width={Math.round(p.w)}
              height={Math.round(p.h)}
              viewBox={`${-p.w/2} ${-p.h} ${p.w} ${p.h}`}
              style={{ display:'block' }}
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* Organic leaf/petal: tip at (0,0), crown at (0,-h) */}
              <path
                d={[
                  `M 0 0`,
                  `C ${p.w*0.48} ${-p.h*0.28}  ${p.w*0.46} ${-p.h*0.68}  0 ${-p.h}`,
                  `C ${-p.w*0.46} ${-p.h*0.68}  ${-p.w*0.48} ${-p.h*0.28}  0 0`,
                  `Z`,
                ].join(' ')}
                fill={p.color}
                opacity={p.opacity}
              />
            </svg>
          </div>
        </div>
      ))}
    </div>
  )
}
